import { api } from '../api/client';
import { API_COLLECTIONS_URL } from '../utils';

/**
 * WHO SEES AND WHO EDITS EACH ELEMENT — a collection's element rules
 * (KPR-509; keepr-api docs/SCHEMA.md § Element grants, KPR-504's endpoints;
 * owner rulings e01–e09 on KPE-019).
 *
 * - `GET /api/collections/{id}/element-grants?card_id=` (manage) →
 *   `{ enabled, grants: [ElementGrant], inherited? }`: with `card_id`, the
 *   rules on that card AND its ancestor cards (the rows that can govern its
 *   records). `enabled` is the server's switch (`ELEMENT_GRANTS`).
 *   `inherited` (KPR-623; keepr-api § What the collection above gives): for
 *   a manager who can save a rule, what the collection ABOVE gives each
 *   element of the card, as levels per audience — the most a rule here may
 *   give (409 `element_rule_widens` beyond it). Never names a person or a
 *   group of the rule above. `[]` on a top-level collection or with nothing
 *   ruled above; absent from an older API, a key, an unconfirmed address.
 * - `PUT …/element-grants/{cardId}/{elementId}` (manage, a session, a
 *   verified address) with `{ rules, keeprOnly }` — a COMPLETE statement,
 *   answered with the ElementGrant.
 * - `DELETE …/element-grants/{cardId}/{elementId}` → `{ ok, deleted }`:
 *   the element follows the card again (or the rule of a card above it).
 *
 * Refusals keep `error.response.data` for the caller to word (409
 * `element_rule_conflicts { conflicts }`, `element_grants_off`,
 * `concurrent_change`; 400 `invalid_audience`, `manage_cannot_hide`,
 * `unknown_group`, `unknown_user`, `too_many_rules`, …).
 */

const grantsUrl = (collectionId, suffix = '') =>
	`${API_COLLECTIONS_URL}/${encodeURIComponent(collectionId)}/element-grants${suffix}`;

/**
 * The rules that can govern a card's records in one collection — or `null`
 * when this API has no element rules to offer the caller: an API that
 * predates them (404 on the route), one with the switch off (`enabled:
 * false`), or a caller who does not manage the collection (403/404). Every
 * one of those means the same thing to the UI: no Access door. A network
 * failure or a 5xx is thrown — the caller says it could not load, rather
 * than pretending the feature is absent.
 *
 * Why `null` and not `{ enabled: false }`: nothing downstream should be able
 * to render a half-door from a shape that "looks" like rules. The only
 * truthy answer is a usable one.
 */
export async function getElementGrants(collectionId, cardId) {
	if (!collectionId) return null;
	try {
		const { data } = await api.get(grantsUrl(collectionId), { params: cardId ? { card_id: cardId } : {} });
		if (!data || typeof data !== 'object' || data.enabled !== true) return null;
		return {
			enabled: true,
			grants: Array.isArray(data.grants) ? data.grants : [],
			// null, not []: "the API said nothing" (an older API, a key) must
			// not read as "nothing is ruled above".
			inherited: Array.isArray(data.inherited) ? data.inherited : null,
		};
	} catch (error) {
		const status = error?.response?.status;
		if (status === 404 || status === 403 || status === 405) return null;
		throw error;
	}
}

/** Save one element's rules: `{ rules, keeprOnly }`, the whole statement. */
export async function putElementGrant(collectionId, cardId, elementId, { rules = [], keeprOnly = false } = {}) {
	const { data } = await api.put(
		grantsUrl(collectionId, `/${encodeURIComponent(cardId)}/${encodeURIComponent(elementId)}`),
		{ rules, keeprOnly: keeprOnly === true }
	);
	return data;
}

/** Back to the card's access (idempotent). */
export async function deleteElementGrant(collectionId, cardId, elementId) {
	const { data } = await api.delete(grantsUrl(collectionId, `/${encodeURIComponent(cardId)}/${encodeURIComponent(elementId)}`));
	return data;
}
